import React, { useState } from 'react';
import jsPDF from 'jspdf';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';


// Accessing jsPDF from the global window object to avoid build errors.

export default function App({ messages }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Helper function to format chat to a string for TXT and MD
  const formatChatToString = (format = 'txt') => {
    if (!messages) return '';
    return messages
      .filter((msg) => msg.role !== 'user' || msg.visible !== false)
      .map(msg => {
        const speaker = msg.role === 'user' ? 'You' : 'Assistant';
        if (format === 'md') {
          return `**${speaker}:**\n\n> ${msg.content}\n\n---\n`;
        }
        return `${speaker}:\n${msg.content}\n\n`;
      }).join('');
  };

  // Helper function to trigger file downloads
  const triggerDownload = (content, fileName, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Main export handler with enhanced PDF formatting
  const handleExport = (format) => {
    if (!messages || messages.length === 0) {
      console.warn("There's nothing to export!");
      return;
    }
    const fileName = `chat-export-${new Date().toISOString().split('T')[0]}`;

    if (format === 'pdf') {
      const doc = new jsPDF();
      
      // --- PDF Styling Constants ---
      const margin = 15;
      const bubblePadding = 5;
      const bubbleMargin = 5;
      const fontSize = 10;
      const lineHeight = 6;
      const userBubbleColor = '#dcf8c6'; // Light green for user
      const assistantBubbleColor = '#f1f0f0'; // Light gray for assistant
      
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const usableWidth = pageWidth - (margin * 2);
      const maxBubbleWidth = usableWidth * 0.8; // Bubbles don't take full width

      let cursorY = margin;
      let pageNumber = 1;

      // --- Helper: Add Header ---
      const addHeader = () => {
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text("Chat Export", margin, margin);
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        doc.text(new Date().toLocaleDateString(), pageWidth - margin, margin, { align: 'right' });
        doc.setDrawColor(150);
        doc.line(margin, margin + 5, pageWidth - margin, margin + 5);
        cursorY = margin + 15;
      };

      // --- Helper: Add Footer ---
      const addFooter = () => {
        doc.setFontSize(8);
        doc.text(`Page ${pageNumber}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
      };

      // --- Helper: Add New Page ---
      const addNewPage = () => {
        addFooter();
        doc.addPage();
        pageNumber++;
        addHeader();
      };
      
      addHeader(); // Initial header

      // --- Process each message ---
      messages.forEach((msg) => {
        const isUser = msg.role === 'user';
        const speaker = isUser ? 'You' : 'Assistant';
        
        // --- FIX: More aggressive sanitization to remove a wider range of special characters ---
        const cleanContent = msg.content.replace(/(\*\*|---|`|#+\s|&-p|ØÜI|Ø=ÜI|Ø<BÆ|\$\S+\$|✅|⚖️|✨|⚡|👉|📌|🚀|📝|📋|🏆|🌟|###\s?|'|’)/g, '').trim();

        if (!cleanContent) return; // Skip empty messages after cleaning

        doc.setFontSize(fontSize);
        const textLines = doc.splitTextToSize(cleanContent, maxBubbleWidth - (bubblePadding * 2));
        const bubbleHeight = (textLines.length * lineHeight) + (bubblePadding * 2) + lineHeight; // Extra space for speaker

        // Check if bubble fits on the page
        if (cursorY + bubbleHeight > pageHeight - margin - 15) {
          addNewPage();
        }

        const bubbleX = isUser ? pageWidth - margin - maxBubbleWidth : margin;
        
        // Draw the chat bubble
        doc.setFillColor(isUser ? userBubbleColor : assistantBubbleColor);
        doc.roundedRect(bubbleX, cursorY, maxBubbleWidth, bubbleHeight, 3, 3, 'F');
        
        // Draw the speaker's name
        doc.setFont(undefined, 'bold');
        doc.text(speaker, bubbleX + bubblePadding, cursorY + bubblePadding + 4);

        // Draw the message content
        doc.setFont(undefined, 'normal');
        doc.text(textLines, bubbleX + bubblePadding, cursorY + bubblePadding + lineHeight + 4);

        cursorY += bubbleHeight + bubbleMargin;
      });

      addFooter(); // Add footer to the last page
      doc.save(`${fileName}.pdf`);

    } else {
      // Handle TXT and MD formats
      const content = formatChatToString(format);
      const mimeType = format === 'md' ? 'text/markdown' : 'text/plain';
      triggerDownload(content, `${fileName}.${format}`, mimeType);
    }
    setIsMenuOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="relative flex items-center bg-black text-white px-3 py-2 rounded-md hover:bg-gray-800 transition-colors"
      >
        <DocumentArrowDownIcon className="h-5 w-5" />
      </button>

      {isMenuOpen && (
        <div
          className="absolute right-0 top-full mt-2 w-36 origin-top-right rounded-md backdrop-blur-md bg-black/80 border border-white/20 shadow-lg z-50 p-1"
        >
          <button type="button" onClick={() => handleExport('txt')} className="w-full text-left block px-3 py-1.5 text-sm text-white hover:bg-purple-600 rounded-md">Export as .txt</button>
          <button type="button" onClick={() => handleExport('md')} className="w-full text-left block px-3 py-1.5 text-sm text-white hover:bg-purple-600 rounded-md">Export as .md</button>
          <button type="button" onClick={() => handleExport('pdf')} className="w-full text-left block px-3 py-1.5 text-sm text-white hover:bg-purple-600 rounded-md">Export as .pdf</button>
        </div>
      )}
    </div>
  );
}